// Copyright (c) StrangeBee 2023

import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { ICONS } from '@Constants/iconsApp';
import { classList } from '@Helpers/className';
import { useLocalMessage } from '@Helpers/locale';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { PageEventList } from '@Organisms/KnowledgeBase/eventList';
import { KnowledgeBaseCreate } from '@Organisms/KnowledgeBase/KnowledgeBaseCreate/KnowledgeBaseCreate';
import { Button, Modal } from 'antd';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { PageInput, PageOutput } from 'thehive-sdk';
import { MarkdownVisualizer } from './MarkdownVisualizer';

interface MarkdownVisualizerModalProps {
    onVisible: (...args: any[]) => any;
    className?: string;
    onEdit: (page: Partial<PageInput>, pageId: string) => any;
    CreateButton: (...args: any[]) => any;
    onDelete: (pageId: string) => any;
    list: PageOutput[];
    hasPermissions?: boolean;
    onCreate: PageEventList['onCreate'];
}

export const MarkdownVisualizerModal = ({
    onVisible,
    className,
    CreateButton,
    list,
    onDelete,
    onEdit,
    hasPermissions = false,
    onCreate,
}: MarkdownVisualizerModalProps): ReactElement => {
    const i = useLocalMessage();
    const [modalVisible, setModalVisible] = useState(false);
    const allCategories = useMemo(
        () => list.reduce((acc, { category }) => (acc.includes(category) ? acc : [...acc, category]), [] as string[]),
        [list],
    );

    useEffect(() => {
        if (modalVisible) {
            onVisible();
        }
    }, [modalVisible]); // eslint-disable-line

    return (
        <>
            <THButton
                type="link"
                className={classList('markdown-visualizer-view__button', className)}
                onClick={(): void => setModalVisible(true)}
            >
                <ICONS.ReadOutlined />
            </THButton>
            <Modal
                getContainer={false}
                className={classList('markdown-visualizer-view__modal', className)}
                width="90vw"
                visible={modalVisible}
                closable={false}
                destroyOnClose
                title={
                    <>
                        <div className="markdown-visualizer-view__modal__header__title">{i('page.view.title')}</div>

                        <div className="markdown-visualizer-view__modal__header__close">
                            <Button
                                type="primary"
                                onClick={(): void => setModalVisible(false)}
                                icon={<ICONS.CloseIcon />}
                            />
                        </div>
                    </>
                }
            >
                {list.length ? (
                    <MarkdownVisualizer
                        hasPermissions={hasPermissions}
                        CreateButton={CreateButton}
                        list={list}
                        onDelete={onDelete}
                        onEdit={onEdit}
                        modalVisible={modalVisible}
                        className={className}
                    />
                ) : (
                    <DisplayEmptyData label={i('page.none')}>
                        {hasPermissions && (
                            <KnowledgeBaseCreate
                                onCreate={(page) => onCreate(page)}
                                allCategories={allCategories}
                                triggerElem={<THButton type="link">{i('page.create.drawer.label')}</THButton>}
                            />
                        )}
                    </DisplayEmptyData>
                )}
            </Modal>
        </>
    );
};
